import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Account } from 'layout/header/components/Account';
import { CloseX } from 'shared/icons/CloseX';
import { ConditionalWrapper } from 'shared/components/ConditionalWrapper';
import { DeprecatedHeaderNavigationList } from 'layout/header/components/DeprecatedHeaderNavigationList';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { LOCALES } from 'localization/constants/configEnums';
import { t } from 'localization/utils/t';
import { showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { trackHeaderNavClick } from 'layout/header/utils/trackHeaderNavClick';
import { useOnRouteChangeComplete } from 'shared/hooks/useOnRouteChangeComplete';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';

const TP = 'layout.header.components.MobileNavigationPanel';

interface IDeprecatedMobileNavigationPanel {
  calendarLinks: ICMSBreadcrumb[];
  closeMobileNavMenu: () => void;
  headerItemsCount: number;
  headerLinks: ICMSBreadcrumb[];
  mobileNavPanelBodyRef: React.RefObject<HTMLDivElement>;
}

export const DeprecatedMobileNavigationPanel = ({
  calendarLinks,
  closeMobileNavMenu,
  headerItemsCount,
  headerLinks,
}: IDeprecatedMobileNavigationPanel) => {
  const { isMenuVisible } = useStoreSelector((state) => state.pageLayout);
  const navBodyRef = useRef<HTMLDivElement>();
  useOnRouteChangeComplete(closeMobileNavMenu);
  const { pathname, locale } = useRouter();
  const shouldOpenAccountTab = pathname.split('/')?.[1] === 'account';

  const NAV_MAP = {
    SHOP_ALL: {
      label: t(`${TP}.shopAll`, 'Shop All'),
      key: 'shopAll',
    },
    ...(locale !== LOCALES.frFR && {
      EDITORIAL: {
        label: t(`${TP}.editorial`, 'Editorial'),
        key: 'editorial',
        link: '/editorial',
      },
    }),
    TIMELINE: {
      label: t(`${TP}.timeline`, 'Timeline'),
      key: 'timeline',
    },
    ACCOUNT: {
      label: t(`${TP}.account`, 'Account'),
      key: 'account',
    },
  };

  const [selectedTab, setSelectedTab] = useState(
    shouldOpenAccountTab ? NAV_MAP.ACCOUNT.key : NAV_MAP.SHOP_ALL.key,
  );

  useEffect(() => {
    if (isMenuVisible) {
      disableBodyScroll(navBodyRef.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isMenuVisible]);

  useEffect(() => {
    setSelectedTab(shouldOpenAccountTab ? NAV_MAP.ACCOUNT.key : NAV_MAP.SHOP_ALL.key);
  }, [isMenuVisible]);

  const handleLinkClick = (tab) => () => {
    setSelectedTab(tab);
    trackHeaderNavClick(tab);
  };

  return (
    <DeprecatedMobileNavigationPanel.Wrapper
      data-qa="mobile_navigation_panel"
      $isMenuVisible={isMenuVisible}
    >
      <div className="fixedHeader">
        <div className="linksWrapper">
          {Object.values(NAV_MAP).map(
            ({ key, label, link = null }: { key: string; label: string; link?: string }) => (
              <ConditionalWrapper
                if={!!link}
                with={(children) => <Link href={link}>{children}</Link>}
                key={key}
              >
                <DeprecatedMobileNavigationPanel.HeaderLink
                  $isSelectedTab={selectedTab === key}
                  onClick={handleLinkClick(key)}
                  data-qa={`nav_${label?.toLowerCase()}_link`}
                >
                  {label}
                </DeprecatedMobileNavigationPanel.HeaderLink>
              </ConditionalWrapper>
            ),
          )}
        </div>
        <button
          aria-label="close"
          className="closeButton"
          data-qa="close"
          type="button"
          onClick={closeMobileNavMenu}
        >
          <CloseX fill="white" size={10} />
        </button>
      </div>

      <DeprecatedMobileNavigationPanel.Body ref={navBodyRef} $isMenuVisible={isMenuVisible}>
        {selectedTab === NAV_MAP.SHOP_ALL.key && (
          <div className="headerNavigationList">
            <DeprecatedHeaderNavigationList
              href="/search"
              menuItems={headerLinks}
              originalLabel="Shop"
              label={t(
                `${TP}.shopNumOfListings`,
                'Shop {numOfListings, plural, =1 {1 Listing} other {# Listings}}',
                {
                  numOfListings: headerItemsCount,
                },
              )}
              specialNavLabel={t(`${TP}.shop`, 'Shop')}
            />
          </div>
        )}
        {selectedTab === NAV_MAP.TIMELINE.key && (
          <div className="headerNavigationList">
            <DeprecatedHeaderNavigationList
              href="/timeline"
              menuItems={calendarLinks}
              originalLabel="Timeline"
              label={t(`${TP}.timelineSneakerReleases`, 'Timeline: Sneaker Releases')}
            />
          </div>
        )}
        {selectedTab === NAV_MAP.ACCOUNT.key && (
          <div className="accountMenu">
            <Account />
          </div>
        )}
      </DeprecatedMobileNavigationPanel.Body>
    </DeprecatedMobileNavigationPanel.Wrapper>
  );
};

const headerAnimation = keyframes`
  0% {
    transform: translateX(-40px);
  }
  100% {
    transform: translateX(0);
  }
`;

const bodyAnimation = keyframes`
  0% {
    transform: translatex(-60px)
  }
  100% {
    transform: translatex(0)
  }
`;

DeprecatedMobileNavigationPanel.Wrapper = styled.div<{ $isMenuVisible: boolean }>`
  ${({ $isMenuVisible }) =>
    showOnMobileAndTabletOnly`
    visibility: ${$isMenuVisible ? 'visible' : 'hidden'};
    opacity: ${$isMenuVisible ? '1' : '0'};
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.black};
    color: ${({ theme }) => theme.colors.white};
    z-index: ${({ theme }) => theme.zIndex.pageOverlay};
    transition: all 0.3s ease;
  `};

  .closeButton {
    padding: 10px;

    :active {
      text-decoration: underline;
    }

    @media (hover: hover) {
      :hover {
        text-decoration: underline;
      }
    }
  }

  .fixedHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 16px 12px 16px;
    ${variableFontRegular({ size: '11.5px', height: '11px' })};
    opacity: 0;

    ${({ $isMenuVisible }) =>
      $isMenuVisible &&
      css`
        animation: ${headerAnimation} 1s ease;
        opacity: 1;
        transition: opacity 0.5s linear;
      `}
  }

  .linksWrapper {
    display: flex;
  }
`;

DeprecatedMobileNavigationPanel.HeaderLink = styled.button<{ $isSelectedTab: boolean }>`
  margin-right: 24px;
  ${({ $isSelectedTab }) =>
    $isSelectedTab &&
    css`
      text-decoration: underline;
      text-underline-offset: 2px;
    `}

  :hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

DeprecatedMobileNavigationPanel.Body = styled.nav<{ $isMenuVisible: boolean }>`
  flex: 1;
  ${variableFontRegular({ size: '11.5px' })};
  overflow-y: auto;
  opacity: 0;

  ${({ $isMenuVisible }) =>
    $isMenuVisible &&
    css`
      animation: ${bodyAnimation} 1s ease;
      opacity: 1;
      transition: opacity 0.5s linear 0.3s;
    `}

  .accountMenu {
    margin: 20px 20px 40px;
  }

  .headerNavigationList {
    padding: 16px;
  }
`;
